import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { usePermissions } from '@/hooks/usePermissions';
import { useCanCreateTemplates } from '@/hooks/useTemplatePermissions';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { toArray } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileText, 
  Save, 
  Copy, 
  Download, 
  Upload, 
  ArrowLeft, 
  ArrowRight,
  CheckCircle,
  Settings,
  Eye,
  Layout,
  BarChart3,
  Plus,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { ResponsiveBreadcrumb } from '@/components/breadcrumb';
import { MetaFieldsEditor } from './MetaFieldsEditor';
import { ItemPalette } from './ItemPalette';
import { ItemEditor } from './ItemEditor';
import { VisibilityEditor } from './VisibilityEditor';
import { PreviewPane } from './PreviewPane';
import { JsonPanel } from './JsonPanel';
import { StickyStepper } from './StickyStepper';
import { LivePreviewPanel } from './LivePreviewPanel';
import { ActionBar } from './ActionBar';
import { AddItemModal } from './AddItemModal';
import { ItemNavigator } from './ItemNavigator';
import { ItemEditorCard } from './ItemEditorCard';
import { DraggableItemList } from './DraggableItemList';
import { 
  TemplateContent, 
  zTemplateContent, 
  createEmptyTemplateContent,
  generateItemId 
} from '../../../../src/schemas/templateContent';
import type { Template } from '../../../../shared/schema';

interface BuilderPageProps {
  templateId?: string;
  mode?: 'create' | 'edit' | 'clone';
}

const STEPS = [
  { id: 'details', label: 'Details', icon: FileText },
  { id: 'structure', label: 'Structure', icon: Layout },
  { id: 'preview', label: 'Preview', icon: Eye },
  { id: 'visibility', label: 'Visibility', icon: Settings },
  { id: 'review', label: 'Review & Save', icon: CheckCircle }
];

export function BuilderPage({ templateId, mode = 'create' }: BuilderPageProps) {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // (debug logs removed - keeping error instrumentation)
  
  // Check permissions using the delegation system
  // useCanCreateTemplates returns a boolean (via select), so read it as such.
  const { data: canCreateTemplates, isLoading: permissionLoading } = useCanCreateTemplates();
  const hasCreatePermission = Boolean(canCreateTemplates);
  
  // ALL useState hooks MUST be declared before conditional returns
  const [activeStep, setActiveStep] = useState('details');
  const [templateData, setTemplateData] = useState({
    name: '',
    description: '',
    category_id: 1,
    type_id: 1,
    content: createEmptyTemplateContent(),
    owner_type: 'Organization' as const,
    owner_org: null as number | null,
    owner_home: null as number | null,
    is_active: true,
    hidden_from: [] as number[]
  });
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [isMetadataOpen, setIsMetadataOpen] = useState(() => {
    const saved = localStorage.getItem('templateBuilder.metadataOpen');
    return saved === 'true' ? true : false;
  });

  // ALL useQuery hooks MUST be declared before conditional returns
  // Load existing template if editing
  const { data: existingTemplate } = useQuery<Template>({
    queryKey: ['/api/templates', templateId],
    queryFn: async () => {
      const response = await fetch(`/api/templates/${templateId}`);
      if (!response.ok) {
        throw new Error('Failed to fetch template');
      }
      return response.json();
    },
    enabled: !!templateId && (mode === 'edit' || mode === 'clone') && !permissionLoading && hasCreatePermission
  });

  // Load template categories
  const { data: categories = [] } = useQuery({
    queryKey: ['/api/template-categories'],
    enabled: !permissionLoading && hasCreatePermission
  });

  // Load template types
  const { data: types = [] } = useQuery({
    queryKey: ['/api/template-types'],
    enabled: !permissionLoading && hasCreatePermission
  });

  // Load organizations and homes for visibility settings
  const { data: organizationsResp } = useQuery({
    queryKey: ['/api/organizations'],
    enabled: !permissionLoading && hasCreatePermission
  });

  const { data: homesResp } = useQuery({
    queryKey: ['/api/homes'],
    enabled: !permissionLoading && hasCreatePermission
  });
  
  // Normalize API responses: handle both array format and {total, items} format
  const organizations = toArray(organizationsResp);
  const homes = toArray(homesResp);
  
  // Permission guard using useEffect to avoid render-time redirect
  // Only redirect if we're certain the user doesn't have permissions AND permission loading is complete
  useEffect(() => {
    try {
      if (!permissionLoading && hasCreatePermission === false) {
        setLocation('/templates');
      }
    } catch (err) {
      // Log detailed error for debugging to avoid silent React minified errors
      // eslint-disable-next-line no-console
      console.error('[BuilderPage] error in permission guard useEffect', err);
    }
  }, [hasCreatePermission, permissionLoading, setLocation]);

  // Return early if permissions are loading or user doesn't have permissions
  if (permissionLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Checking permissions...</p>
        </div>
      </div>
    );
  }
  
  if (!hasCreatePermission) {
    return null;
  }

  // Initialize template data when editing/cloning
  useEffect(() => {
    try {
      if (existingTemplate && (mode === 'edit' || mode === 'clone')) {
      const rawContent = existingTemplate.content ? existingTemplate.content as any : {};
      // Ensure items array always exists
      const content: TemplateContent = {
        ...createEmptyTemplateContent(),
        ...rawContent,
        items: rawContent.items || []
      };
      
      setTemplateData({
        name: mode === 'clone' ? `${existingTemplate.name} (Copy)` : existingTemplate.name,
        description: existingTemplate.description || '',
        category_id: existingTemplate.category_id,
        type_id: existingTemplate.type_id,
        content: content,
        owner_type: existingTemplate.owner_type as 'Organization',
        owner_org: existingTemplate.owner_org,
        owner_home: existingTemplate.owner_home,
        is_active: existingTemplate.is_active,
        hidden_from: (existingTemplate.hidden_from as number[]) || []
      });

      // Set first item as active if items exist
      if (content.items && content.items.length > 0) {
        setActiveItemId(content.items[0].id);
      }
    }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[BuilderPage] error initializing template data', err, { existingTemplate, mode });
    }
  }, [existingTemplate, mode]);

  // Save metadata collapse state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('templateBuilder.metadataOpen', String(isMetadataOpen));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[BuilderPage] failed to persist metadata open state', err);
    }
  }, [isMetadataOpen]);

  // end

  // Save template mutation
  const saveTemplateMutation = useMutation({
    mutationFn: async (data: typeof templateData) => {
      const url = mode === 'edit' ? `/api/templates/${templateId}` : '/api/templates';
      const method = mode === 'edit' ? 'PATCH' : 'POST';
      
      const response = await apiRequest(method, url, data);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Template saved',
        description: `Template has been ${mode === 'edit' ? 'updated' : 'created'} successfully.`
      });
      queryClient.invalidateQueries({ queryKey: ['/api/templates'] });
      // Also invalidate available templates list used by templates listing page
      queryClient.invalidateQueries({ queryKey: ['/api/templates/available'] });
      if (mode === 'edit' && templateId) {
        queryClient.invalidateQueries({ queryKey: ['/api/templates', templateId] });
      }
      setLocation('/templates');
    },
    onError: (error: any) => {
      toast({
        title: 'Save failed',
        description: error.message || 'Failed to save template',
        variant: 'destructive'
      });
    }
  });

  // Clone template mutation
  const cloneTemplateMutation = useMutation({
    mutationFn: async (cloneData: { owner_type: string; owner_org?: number; owner_home?: number }) => {
      const response = await apiRequest('POST', `/api/templates/${templateId}/clone`, cloneData);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Template cloned',
        description: 'Template has been cloned successfully.'
      });
      queryClient.invalidateQueries({ queryKey: ['/api/templates'] });
      queryClient.invalidateQueries({ queryKey: ['/api/templates/available'] });
      setLocation('/templates');
    }
  });

  const handleSave = () => {
    // Validate template content structure
    const validation = zTemplateContent.safeParse(templateData.content);
    if (!validation.success) {
      toast({
        title: 'Validation failed',
        description: 'Please fix the template structure errors.',
        variant: 'destructive'
      });
      setActiveStep('structure'); // Navigate to structure step
      return;
    }

    // Validate visibility settings
    const visibilityErrors: string[] = [];
    
    if (!templateData.name || templateData.name.trim() === '') {
      visibilityErrors.push('Template name is required');
    }
    
    if ((templateData.owner_type as string) === 'Organization' && !templateData.owner_org) {
      visibilityErrors.push('Organization selection is required');
    }
    
    if ((templateData.owner_type as string) === 'Home' && !templateData.owner_home) {
      visibilityErrors.push('Home selection is required');
    }

    if (visibilityErrors.length > 0) {
      toast({
        title: 'Validation failed',
        description: visibilityErrors.join(', '),
        variant: 'destructive'
      });
      setActiveStep('visibility'); // Navigate to visibility step
      return;
    }

    saveTemplateMutation.mutate(templateData);
  };

  const handleClone = () => {
    if (mode === 'clone') {
      cloneTemplateMutation.mutate({
        owner_type: templateData.owner_type,
        owner_org: templateData.owner_org || undefined,
        owner_home: templateData.owner_home || undefined
      });
    }
  };

  const handleExportJson = () => {
    const dataStr = JSON.stringify(templateData.content, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${templateData.name || 'template'}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const updateTemplateData = (updates: Partial<typeof templateData>) => {
    setTemplateData(prev => ({ ...prev, ...updates }));
  };

  const updateContent = (content: TemplateContent) => {
    setTemplateData(prev => ({ ...prev, content }));
  };

  const getCurrentStepIndex = () => STEPS.findIndex(step => step.id === activeStep);
  const canGoNext = () => getCurrentStepIndex() < STEPS.length - 1;
  const canGoPrev = () => getCurrentStepIndex() > 0;

  const goNext = () => {
    if (canGoNext()) {
      const nextStep = STEPS[getCurrentStepIndex() + 1];
      setActiveStep(nextStep.id);
    }
  };

  const goPrev = () => {
    if (canGoPrev()) {
      const prevStep = STEPS[getCurrentStepIndex() - 1];
      setActiveStep(prevStep.id);
    }
  };

  // Handler functions for the new focused item editor flow
  const handleAddItem = (item: any) => {
    const newContent = { 
      ...templateData.content, 
      items: [...templateData.content.items, item] 
    };
    updateContent(newContent);
    setActiveItemId(item.id);
  };

  const handleUpdateActiveItem = (updatedItem: any) => {
    // Recursive function to update items at any depth
    const updateItemRecursive = (items: any[]): any[] => {
      return items.map((item: any) => {
        if (item.id === activeItemId) {
          return updatedItem;
        }
        if (item.children && item.children.length > 0) {
          return {
            ...item,
            children: updateItemRecursive(item.children)
          };
        }
        return item;
      });
    };

    const newItems = updateItemRecursive(templateData.content.items);
    updateContent({ ...templateData.content, items: newItems });
  };

  // Helper function to find an item recursively
  const findItemRecursive = (items: any[], targetId: string): any | null => {
    for (const item of items) {
      if (item.id === targetId) return item;
      if (item.children && item.children.length > 0) {
        const found = findItemRecursive(item.children, targetId);
        if (found) return found;
      }
    }
    return null;
  };

  const handleDuplicateItem = () => {
    if (!activeItemId) return;
    const itemToDuplicate = findItemRecursive(templateData.content.items, activeItemId);
    if (!itemToDuplicate) return;

    const duplicatedItem = {
      ...itemToDuplicate,
      id: generateItemId(),
      label: `${itemToDuplicate.label} (copy)`,
      children: itemToDuplicate.children || []
    };

    const newContent = { 
      ...templateData.content, 
      items: [...templateData.content.items, duplicatedItem] 
    };
    updateContent(newContent);
    setActiveItemId(duplicatedItem.id);
    toast({ description: 'Item duplicated successfully' });
  };

  const handleDeleteItem = () => {
    if (!activeItemId) return;
    
    // Recursive function to delete items at any depth
    const deleteItemRecursive = (items: any[]): any[] => {
      return items.filter((item: any) => {
        if (item.id === activeItemId) return false;
        if (item.children && item.children.length > 0) {
          item.children = deleteItemRecursive(item.children);
        }
        return true;
      });
    };

    const newItems = deleteItemRecursive(templateData.content.items);
    updateContent({ ...templateData.content, items: newItems });
    
    // Set next active item
    if (newItems.length > 0) {
      setActiveItemId(newItems[0].id);
    } else {
      setActiveItemId(null);
    }
    
    toast({ description: 'Item deleted successfully' });
  };

  // New handlers for draggable list
  const handleItemsReorder = (newItems: any[]) => {
    updateContent({ ...templateData.content, items: newItems });
  };

  const handleMoveUp = (itemId: string) => {
    const index = templateData.content.items.findIndex((item: any) => item.id === itemId);
    if (index <= 0) return;
    
    const newItems = [...templateData.content.items];
    [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
    updateContent({ ...templateData.content, items: newItems });
  };

  const handleMoveDown = (itemId: string) => {
    const index = templateData.content.items.findIndex((item: any) => item.id === itemId);
    if (index === -1 || index >= templateData.content.items.length - 1) return;
    
    const newItems = [...templateData.content.items];
    [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
    updateContent({ ...templateData.content, items: newItems });
  };

  const handleDuplicateItemById = (itemId: string) => {
    const itemToDuplicate = templateData.content.items.find((item: any) => item.id === itemId);
    if (!itemToDuplicate) return;

    const duplicatedItem = {
      ...itemToDuplicate,
      id: generateItemId(),
      label: `${itemToDuplicate.label} (copy)`,
      children: itemToDuplicate.children || []
    };

    const newContent = { 
      ...templateData.content, 
      items: [...templateData.content.items, duplicatedItem] 
    };
    updateContent(newContent);
    toast({ description: 'Item duplicated successfully' });
  };

  const handleDeleteItemById = (itemId: string) => {
    const newItems = templateData.content.items.filter((item: any) => item.id !== itemId);
    updateContent({ ...templateData.content, items: newItems });
    
    if (activeItemId === itemId) {
      setActiveItemId(newItems.length > 0 ? newItems[0].id : null);
    }
    
    toast({ description: 'Item deleted successfully' });
  };

  const handleAddSubAnswer = (parentId: string) => {
    const newSubItem: any = {
      id: generateItemId(),
      label: 'New Sub-Answer',
      type: 'text',
      actions_enabled: false,
      children: []
    };

    const addSubItemRecursive = (items: any[]): any[] => {
      return items.map((item: any) => {
        if (item.id === parentId) {
          return {
            ...item,
            children: [...(item.children || []), newSubItem]
          };
        }
        if (item.children && item.children.length > 0) {
          return {
            ...item,
            children: addSubItemRecursive(item.children)
          };
        }
        return item;
      });
    };

    const newItems = addSubItemRecursive(templateData.content.items);
    updateContent({ ...templateData.content, items: newItems });
    setActiveItemId(newSubItem.id);
    toast({ description: 'Sub-answer added successfully' });
  };

  return (
    <div className="clean-ui pb-24">
      <ResponsiveBreadcrumb 
        items={[
          { label: 'Templates', href: '/templates' },
          { label: mode === 'create' ? 'New Template' : mode === 'clone' ? 'Clone Template' : 'Edit Template' }
        ]} 
        className="mb-6" 
      />

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-foreground mb-2" data-testid="page-title">
          {mode === 'create' ? 'Create Template' : mode === 'clone' ? 'Clone Template' : 'Edit Template'}
        </h1>
        <p className="text-muted-foreground">
          Build custom audit templates with scoring and validation
        </p>
      </div>

      {/* Sticky Stepper */}
      <StickyStepper
        steps={STEPS}
        activeStep={activeStep}
        onStepChange={setActiveStep}
        getCurrentStepIndex={getCurrentStepIndex}
      />

      {/* Conditional Layout - Full width for all steps except Review */}
      <div className="mt-6">
        {/* Main Content */}
        <div className="space-y-6">
          <Tabs value={activeStep} onValueChange={setActiveStep}>
            <TabsContent value="details">
              <Card className="shadow-lg">
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl">Template Details</CardTitle>
                  <p className="text-sm text-muted-foreground">Enter the basic information for your template</p>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground">Template Name</label>
                    <input
                      type="text"
                      value={templateData.name}
                      onChange={(e) => updateTemplateData({ name: e.target.value })}
                      className="w-full px-4 py-2.5 border border-input rounded-lg bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                      placeholder="Enter template name"
                      data-testid="input-template-name"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-foreground">Type</label>
                      <Select
                        value={String(templateData.type_id)}
                        onValueChange={(value) => updateTemplateData({ type_id: parseInt(value) })}
                      >
                        <SelectTrigger className="w-full" data-testid="select-template-type">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {(types as any[]).map((type: any) => (
                            <SelectItem key={type.id} value={String(type.id)}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-foreground">Category</label>
                      <Select
                        value={String(templateData.category_id)}
                        onValueChange={(value) => updateTemplateData({ category_id: parseInt(value) })}
                      >
                        <SelectTrigger className="w-full" data-testid="select-template-category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {(categories as any[]).map((category: any) => (
                            <SelectItem key={category.id} value={String(category.id)}>
                              {category.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground">Description</label>
                    <textarea
                      value={templateData.description}
                      onChange={(e) => updateTemplateData({ description: e.target.value })}
                      className="w-full px-4 py-2.5 border border-input rounded-lg bg-background text-foreground text-base focus:outline-none focus:ring-2 focus:ring-ring transition-all"
                      rows={4}
                      placeholder="Enter template description"
                      data-testid="input-template-description"
                    />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="structure">
              <div className="space-y-4">
                {/* Structure Step Header with Add Item Button */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h2 className="text-lg font-semibold">Template Structure</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      Build your audit template with items and questions
                    </p>
                  </div>
                  <Button
                    onClick={() => setIsAddItemModalOpen(true)}
                    data-testid="button-add-item"
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    Add Item
                  </Button>
                </div>

                {/* Collapsible Metadata Section */}
                <Collapsible
                  open={isMetadataOpen}
                  onOpenChange={setIsMetadataOpen}
                  className="w-full"
                >
                  <Card className="shadow-lg">
                    <CollapsibleTrigger className="w-full">
                      <CardHeader className="pb-3 cursor-pointer hover:bg-muted/50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="text-left">
                            <CardTitle className="text-base">Meta Fields Configuration</CardTitle>
                            <p className="text-sm text-muted-foreground mt-0.5">
                              Configure template meta fields and custom properties
                            </p>
                          </div>
                          {isMetadataOpen ? (
                            <ChevronUp className="w-5 h-5 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-muted-foreground" />
                          )}
                        </div>
                      </CardHeader>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <CardContent className="p-6 pt-0">
                        <MetaFieldsEditor 
                          content={templateData.content}
                          onChange={updateContent}
                        />
                      </CardContent>
                    </CollapsibleContent>
                  </Card>
                </Collapsible>

                {/* Empty State */}
                {templateData.content.items.length === 0 ? (
                  <Card className="shadow-lg">
                    <CardContent className="flex flex-col items-center justify-center py-16">
                      <Layout className="w-16 h-16 text-muted-foreground mb-4" />
                      <h3 className="text-xl font-semibold mb-2">No items yet</h3>
                      <p className="text-muted-foreground mb-6 text-center max-w-md">
                        Add your first audit item to start building your template
                      </p>
                      <Button
                        onClick={() => setIsAddItemModalOpen(true)}
                        data-testid="button-add-first-item"
                        className="gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add Item
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left: Collapsible Tree */}
                    <div className="space-y-4">
                      <Card className="shadow-lg">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base">Template Items</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <DraggableItemList
                            items={templateData.content.items}
                            activeItemId={activeItemId}
                            onItemsReorder={handleItemsReorder}
                            onItemSelect={setActiveItemId}
                            onMoveUp={handleMoveUp}
                            onMoveDown={handleMoveDown}
                            onDuplicate={handleDuplicateItemById}
                            onDelete={handleDeleteItemById}
                            onAddSubAnswer={handleAddSubAnswer}
                          />
                        </CardContent>
                      </Card>
                    </div>

                    {/* Right: Item Editor */}
                    <div className="space-y-4">
                      {activeItemId && findItemRecursive(templateData.content.items, activeItemId) ? (
                        <ItemEditorCard
                          item={findItemRecursive(templateData.content.items, activeItemId)!}
                          itemIndex={0}
                          totalItems={templateData.content.items.length}
                          onChange={handleUpdateActiveItem}
                          onDuplicate={handleDuplicateItem}
                          onDelete={handleDeleteItem}
                        />
                      ) : (
                        <Card className="shadow-lg">
                          <CardContent className="flex flex-col items-center justify-center py-12">
                            <Layout className="w-12 h-12 text-muted-foreground mb-3" />
                            <p className="text-muted-foreground text-center">
                              Select an item from the list to edit
                            </p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Add Item Modal */}
              <AddItemModal
                open={isAddItemModalOpen}
                onOpenChange={setIsAddItemModalOpen}
                onAddItem={handleAddItem}
              />
            </TabsContent>

            <TabsContent value="preview">
              <div className="space-y-4">
                <div className="mb-3">
                  <h2 className="text-lg font-semibold">Template Preview</h2>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Live read-only preview of your template
                  </p>
                </div>
                <LivePreviewPanel 
                  templateData={templateData}
                  categories={categories as any[]}
                  types={types as any[]}
                />
              </div>
            </TabsContent>

            <TabsContent value="visibility">
              <VisibilityEditor
                templateData={templateData}
                organizations={organizations as any[]}
                homes={homes as any[]}
                onChange={updateTemplateData}
              />
            </TabsContent>

            <TabsContent value="review">
              <JsonPanel 
                content={templateData.content}
                onChange={updateContent}
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Sticky Action Bar */}
      <ActionBar
        mode={mode}
        canGoPrev={canGoPrev()}
        canGoNext={canGoNext()}
        onPrev={goPrev}
        onNext={goNext}
        onSave={handleSave}
        onExport={handleExportJson}
        onClone={mode === 'clone' ? handleClone : undefined}
        isSaving={saveTemplateMutation.isPending}
        isCloning={cloneTemplateMutation.isPending}
      />
    </div>
  );
}